import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useSwipeToDismiss } from "../hooks/useSwipeToDismiss";
import { useLanguage } from "../i18n/language-provider";
import { Sheet, SheetOverlay, SheetPanel, SheetTitle } from "./ui/sheet";

const InsidePhoneMenuContext = createContext(false);

/**
 * Whether what asks stands inside the phone's menu, a dialog.
 *
 * What the rail opens from inside the menu is drawn outside the menu's box, at
 * the end of the page, and the dialog's scroll lock takes the wheel and the
 * finger of everything out there: a popover held to the window's height could
 * not be scrolled at all (the sync details, measured in Chromium, 5 Oct 2026).
 * Such a popover opens as a modal layer of its own there, with a lock that
 * stands on top of the menu's, and as the plain popover it is beside the page.
 * The rail is drawn twice, so this is a fact about the copy that asks, not
 * about the window's width.
 */
export function useInsidePhoneMenu(): boolean {
    return useContext(InsidePhoneMenuContext);
}

/**
 * Where the rail stands: beside the page from `md` up, and in a menu drawn
 * over the page below that, where the top bar's button opens it.
 *
 * Shared by the rail (`Sidebar`) and by what stands in for it when it cannot
 * be drawn (`RailUnavailable`), so the stand-in is exactly where the rail was
 * and exactly as wide: the page beside it is laid out against that width, and
 * would move as the rail went down and came back. What the menu is lives here
 * too, once for both:
 *
 * - **It is always drawn at full width**, whatever the desktop rail is folded
 *   to, since a strip of unlabelled icons is no way to find a page on a phone.
 * - **It is a dialog** (`PhoneMenu`), so it can be worked from a keyboard and
 *   with a screen reader.
 * - **It is closed once the rail stands beside the page.** Its own class hides
 *   it from `md` up, while whether it is open is the shell's to say. A dialog
 *   holds the focus and takes the page out of reach, and one nobody can see
 *   must not: a window widened past the breakpoint with the menu open, or a
 *   tablet turned on its side, would be left with a page that takes no click.
 *   So from that width up it is not drawn, and the shell is told it is closed.
 */

/**
 * The width from which the rail stands beside the page. Tailwind's `md`, as
 * Tailwind spells it, and not its 768px: a rem in a media query is the
 * browser's own default size, in the stylesheet and in `matchMedia` alike, so
 * this and the `md:` classes below cannot come to disagree where that size is
 * not 16px.
 */
export const RAIL_BESIDE_PAGE_QUERY = "(min-width: 48rem)";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

interface RailFrameProps {
    /** Whether the rail beside the page is folded to its icons. */
    collapsed: boolean;
    /** Whether the phone's menu is open. */
    mobileOpen: boolean;
    onMobileClose?: () => void;
    /** Draws the rail at one width; asked once for each place it stands. */
    children: (collapsed: boolean) => ReactNode;
}

export function RailFrame({ collapsed, mobileOpen, onMobileClose, children }: RailFrameProps) {
    const besidePage = useMediaQuery(RAIL_BESIDE_PAGE_QUERY);

    useEffect(() => {
        if (mobileOpen && besidePage) onMobileClose?.();
    }, [mobileOpen, besidePage, onMobileClose]);

    return (
        <>
            {/* Beside the page: always drawn, hidden on a phone. */}
            <div className="hidden md:block flex-shrink-0">{children(collapsed)}</div>

            {/* The phone's menu, over the page. */}
            {mobileOpen && !besidePage && <PhoneMenu onClose={onMobileClose}>{children(false)}</PhoneMenu>}
        </>
    );
}

/**
 * The rail in a menu over the page, as a dialog.
 *
 * It used to be plain boxes: nothing said what it was, Escape did nothing, the
 * focus stayed on the button that had opened it, under the backdrop, and Tab
 * walked on into the page behind. It is built on the sheet (`ui/sheet`) and
 * not by hand. Escape and the focus would have been a few lines; holding Tab
 * inside and taking the page behind out of reach are the rest of what a dialog
 * is, and they are what the primitive does:
 *
 * - **It says what it is**: a dialog named by the rail's own word for it
 *   (`sidebar.menu`), through a title drawn for a screen reader alone. The
 *   title is no heading, so the rail's first heading is still its brand.
 * - **Escape closes it, and so does a press beside it**, through the one door
 *   the rail's X goes through (`onClose`).
 * - **It takes the focus as it opens, on itself** and not on its first
 *   control. That is the X, whose tooltip opens for any focus no press
 *   brought: a finger would be shown it, and the next Escape would close the
 *   tooltip and leave the menu open. Nothing marks the menu as focused; the
 *   first Tab goes to its first control and shows where the focus is, as with
 *   a heading focused on arrival.
 * - **Tab stays inside it**, round from its last control to its first, and
 *   everything else on the page is out of a screen reader's reach and takes no
 *   press while it is open: the page, the rail beside it, the "Ask Optivo"
 *   window under it.
 *
 * **It is not marked `aria-modal`.** What the rail opens from inside the menu
 * (the sync details, the settings they lead to, a tooltip) is drawn at the end
 * of the page, outside this box, and a screen reader that takes `aria-modal`
 * at its word leaves everything outside the box out of reach, those included.
 * Hiding the rest of the page says the same and leaves them reachable.
 *
 * **It is drawn where it stands**, with no portal: inside the rail's own
 * frame, over the page, as before.
 *
 * **Handing the focus back is the shell's** (`usePhoneMenu`). The button that
 * opens the menu stands in the page's top bar, outside the rail, and the sheet
 * would hand the focus to a trigger of its own, which it has not got. That is
 * also what lets the "Ask Optivo" window, asked for from the menu, keep the
 * caret it takes as the menu closes: the window floats under the menu and
 * outlives it, and the shell hands the focus back only where it went with the
 * menu.
 */
function PhoneMenu({ onClose, children }: { onClose?: () => void; children: ReactNode }) {
    const { t } = useLanguage();
    const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
    const panelRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const swipe = useSwipeToDismiss({ panelRef, overlayRef, onDismiss: () => onClose?.(), reducedMotion });

    return (
        <Sheet
            open
            onOpenChange={(open) => {
                if (!open) onClose?.();
            }}
        >
            <div className="md:hidden fixed inset-0 z-50 flex">
                <SheetOverlay ref={overlayRef} className="optivo-backdrop-in" />
                <SheetPanel
                    ref={panelRef}
                    {...swipe}
                    // No description, and none promised.
                    aria-describedby={undefined}
                    onOpenAutoFocus={(event) => {
                        event.preventDefault();
                        (event.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
                    }}
                    // pan-y here and on the scrollers inside: touch-action resolves at the nearest one.
                    className="relative z-50 outline-none touch-pan-y touch-pinch-zoom optivo-menu-in"
                >
                    <SheetTitle asChild>
                        <span className="sr-only">{t.sidebar.menu}</span>
                    </SheetTitle>
                    <InsidePhoneMenuContext.Provider value>{children}</InsidePhoneMenuContext.Provider>
                </SheetPanel>
            </div>
        </Sheet>
    );
}

/** The rail's column at either width: the dark surface, the border and the 4rem or 16rem it takes. */
export function railColumnClass(collapsed: boolean): string {
    return `bg-sidebar scrollbar-rail border-r border-sidebar-border h-full flex flex-col transition-all duration-200 ${collapsed ? "w-16" : "w-64"}`;
}
